// ── Grouping is a VIEW ───────────────────────────────────────────────
// Rows are never re-parented. This is the whole argument against nesting
// tasks under a client "parent" task: a heading costs nothing, can be
// changed at will, and cannot hide a task inside a collapsed row.
import { TASK_TYPES } from './parse-task.js';

// Exported so callers rendering a task's type elsewhere (e.g. the Client
// grouping's row-level chip in coach-tasks.js) share this exact map instead
// of hand-rolling a second one that can drift out of sync with it.
export const TYPE_LABEL = {
  nutrition: 'Nutrition', workouts: 'Workouts', checkin: 'Check-in',
  message: 'Message', review: 'Review', admin: 'Admin',
};

function dueBucket(due, today) {
  if (!due) return { key: 'zz-none', label: 'No due date' };
  if (due < today) return { key: 'a-overdue', label: 'Overdue' };
  if (due === today) return { key: 'b-today', label: 'Today' };

  const start = new Date(`${today}T00:00:00Z`);

  const tomorrowDate = new Date(start);
  tomorrowDate.setUTCDate(tomorrowDate.getUTCDate() + 1);
  const tomorrow = tomorrowDate.toISOString().slice(0, 10);

  // Upcoming Sunday (0 days out if today is already Sunday) — same rule as
  // the shipped _endOfWeek() in coach-tasks.js, ported to UTC-safe getters
  // so this module stays pure and never reads the system clock.
  const endDate = new Date(start);
  endDate.setUTCDate(endDate.getUTCDate() + ((7 - endDate.getUTCDay()) % 7));
  const endOfWeek = endDate.toISOString().slice(0, 10);

  // Tomorrow is a stronger, more specific signal than "This week";
  // if it's due tomorrow, always return Tomorrow regardless of where the week ends.
  if (due === tomorrow) {
    return { key: 'c-tomorrow', label: 'Tomorrow' };
  }
  return due <= endOfWeek
    ? { key: 'd-week', label: 'This week' }
    : { key: 'e-later', label: 'Later' };
}

export function groupTasks(tasks, mode, { clientName = () => '', today } = {}) {
  const list = Array.isArray(tasks) ? tasks : [];
  if (!list.length) return [];
  const buckets = new Map();

  for (const t of list) {
    let key, label;
    if (mode === 'client') {
      const name = clientName(t.client_id);
      key = name ? `a-${name.toLowerCase()}-${t.client_id}` : 'zz-none';
      label = name || 'No client';
    } else if (mode === 'type') {
      const type = TASK_TYPES.includes(t.category) ? t.category : 'admin';
      // Fixed type order, so the headings do not reshuffle as tasks change.
      key = String(TASK_TYPES.indexOf(type)).padStart(2, '0');
      label = TYPE_LABEL[type];
    } else {
      ({ key, label } = dueBucket(t.due_date, today));
    }
    if (!buckets.has(key)) buckets.set(key, { key, label, tasks: [] });
    buckets.get(key).tasks.push(t);
  }

  return [...buckets.values()].sort((a, b) => a.key.localeCompare(b.key));
}
